import { Link } from 'react-router-dom';
import {
  DeviceMobile,
  ChatCircleDots,
  Sparkle,
  CheckCircle,
  CalendarCheck,
  Phone,
  CaretRight,
  Heart,
  Camera,
  CloudArrowDown,
  PaintBrushBroad,
} from '@phosphor-icons/react';
import { BRAND } from '../../lib/brand';

const PHOTO_BOOTH_IMAGES = {
  hero: 'https://images.squarespace-cdn.com/content/v1/620e8e3b6f7b375c5cbd9d40/1706815826496-QSF0DPJ3QQ674IN0I55O/SalsaPromoHighRes007%2B%281%29%2Bcopy.png',
  ipad: 'https://images.squarespace-cdn.com/content/v1/620e8e3b6f7b375c5cbd9d40/1706815826496-QSF0DPJ3QQ674IN0I55O/SalsaPromoHighRes007%2B%281%29%2Bcopy.png',
  guests: 'https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1200&q=80&auto=format&fit=crop',
};

const features = [
  {
    icon: DeviceMobile,
    title: 'Premium iPad Setup',
    description: 'A sleek, modern iPad station designed to disappear into the décor — not dominate it.',
  },
  {
    icon: ChatCircleDots,
    title: 'Instant Text Delivery',
    description: "Guests enter their number once and every photo they take arrives instantly on their phone. No prints, no lines, no email lists.",
  },
  {
    icon: CloudArrowDown,
    title: 'Cloud Gallery for the Couple',
    description: 'A shared cloud album of every photo taken on the day — yours to keep, share, and post forever.',
  },
  {
    icon: PaintBrushBroad,
    title: 'Custom Overlays & Filters',
    description: 'Add your monogram, wedding date, hashtag, or color palette to every photo for a polished, on-brand look.',
  },
  {
    icon: Camera,
    title: 'Photo · Boomerang · GIF',
    description: 'Guests choose how they want to capture the moment — still photo, looping boomerang, or animated GIF.',
  },
  {
    icon: Sparkle,
    title: 'Unattended & Hassle-Free',
    description: "We set it up, guests run it themselves, and we tear it down at the end. You don't lift a finger.",
  },
];

const faqs = [
  {
    q: 'How is this different from a traditional photo booth?',
    a: "Traditional photo booths require a printer, a hot lamp, a curtained backdrop, and someone babysitting the line. Our digital booth is an iPad on an elegant stand — guests take their photo, type in their number, and it's instantly on their phone. No prints, no lines, no clutter.",
  },
  {
    q: 'Do you require power and Wi-Fi?',
    a: 'Power yes (one standard outlet). Wi-Fi is preferred but we travel with a cellular backup so the booth never goes offline mid-reception.',
  },
  {
    q: 'How much space does the booth need?',
    a: 'About a 4 ft × 4 ft footprint. We can tuck it into a corner, place it near the bar, or set it up beside the dance floor — wherever your guests will naturally gather.',
  },
  {
    q: 'Can you customize the overlay for our wedding?',
    a: 'Absolutely. Send us your monogram, your wedding date, your hashtag, or your color palette and we will design a custom overlay that appears on every photo.',
  },
  {
    q: 'Do we get all the photos after the event?',
    a: 'Yes — every photo, boomerang, and GIF taken during your event is delivered to you in a shared cloud gallery within 24 hours.',
  },
];

export const PhotoBoothPage = () => {
  return (
    <main data-testid="photo-booth-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${PHOTO_BOOTH_IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0000]/95 via-[#800000]/60 to-[#800000]/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <nav className="flex items-center gap-2 text-xs text-white/70 mb-6 tracking-wide">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            <CaretRight size={10} weight="bold" />
            <Link to="/services/wedding-officiant" className="hover:text-[#D4AF37] transition-colors">Wedding Officiant</Link>
            <CaretRight size={10} weight="bold" />
            <span className="text-[#D4AF37]">Digital Photo Booth</span>
          </nav>

          <div className="inline-flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">
              Add-On · Premium Experience
            </span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl font-semibold text-white leading-[1.05] mb-6 max-w-4xl drop-shadow-2xl">
            Sleek Digital <span className="italic gold-shimmer">Photo Booth.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed mb-9 drop-shadow">
            Enhance your special day with a premium, fully digital iPad photo booth. Guests seamlessly capture memories and instantly receive high-quality photos directly to their phones via text. Cumbersome setups and printing lines are gone — just pure, instant, digital fun.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/booking?service=wedding-officiant&photoBooth=1"
              data-testid="photo-booth-hero-book-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-8 py-4 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-2xl"
            >
              <CalendarCheck weight="bold" size={20} />
              Book Wedding + Photo Booth
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-3 border border-white/60 backdrop-blur-sm bg-white/10 text-white hover:bg-white hover:text-[#800000] transition-all px-8 py-4 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={20} />
              {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Intro split */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 border-2 border-[#D4AF37]/40 rounded-sm rotate-2" />
                <img
                  src={PHOTO_BOOTH_IMAGES.ipad}
                  alt="Digital iPad photo booth setup"
                  className="relative w-full aspect-[4/5] object-cover rounded-sm shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white px-5 py-3 shadow-xl border-l-4 border-[#D4AF37] hidden sm:block">
                  <p className="font-serif-display text-base text-[#800000] italic leading-none">
                    Modern. Polished.
                  </p>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C] mt-1.5">
                    Not your aunt's photo booth
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="flex items-center gap-3 text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-5">
                <span className="h-px w-8 bg-[#D4AF37]" />
                Why guests love it
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-7 leading-tight">
                Instant, modern, and <span className="italic">on every guest's phone.</span>
              </h2>
              <div className="space-y-5 text-[#1A1A1A] leading-[1.85] text-base">
                <p>
                  We bring an elegant iPad station to your venue — no printer humming, no crew of operators, no lonely props basket. Guests walk up, snap a photo or boomerang, type in their number, and the photo lands on their phone seconds later.
                </p>
                <p>
                  Every photo is saved to a shared cloud gallery for the couple, with a tasteful custom overlay (monogram, date, hashtag, color story — whatever you'd like). It's the photo booth your venue actually wants in the room.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-24 bg-[#FBF8F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">What's Included</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] leading-tight">
              Premium experience, <span className="italic">zero hassle.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div
                key={i}
                data-testid={`photo-booth-feature-${i}`}
                className="bg-white border border-[#E8E2D5] rounded-sm p-7 hover:border-[#D4AF37]/50 hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-5">
                  <f.icon weight="duotone" size={24} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-serif-display text-lg font-semibold text-[#800000] mb-3 leading-tight">
                  {f.title}
                </h3>
                <p className="text-[#5C5C5C] text-sm leading-relaxed">
                  {f.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lifestyle image strip */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={PHOTO_BOOTH_IMAGES.guests}
                alt="Wedding guests using a digital photo booth"
                className="w-full aspect-[4/3] object-cover rounded-sm shadow-2xl"
              />
            </div>
            <div>
              <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">The Vibe</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] mb-6 leading-tight">
                A keepsake your guests <span className="italic">actually use.</span>
              </h2>
              <p className="text-[#1A1A1A] text-base leading-[1.85] mb-5">
                Printed strips end up in the bottom of a purse and forgotten by Sunday. Instant text-delivered photos end up on Instagram by midnight, in group chats by Monday, and saved to the camera roll forever.
              </p>
              <p className="text-[#1A1A1A] text-base leading-[1.85]">
                Better photos. Bigger reach. Zero waste. And one less thing for you to manage on the wedding day.
              </p>

              <ul className="mt-7 space-y-2.5">
                {['Setup & teardown by us', 'No staff to tip', 'No printer mess to manage', 'All photos in one cloud gallery'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#1A1A1A] text-sm">
                    <CheckCircle weight="fill" size={18} className="text-[#D4AF37]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-[#FBF8F1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">FAQs</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
              Good questions. <span className="italic">Honest answers.</span>
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details
                key={i}
                data-testid={`photo-booth-faq-${i}`}
                className="group bg-white border border-[#E8E2D5] rounded-sm hover:border-[#D4AF37]/50 transition-colors"
              >
                <summary className="cursor-pointer list-none p-6 flex items-center justify-between gap-6">
                  <span className="font-serif-display text-lg sm:text-xl text-[#800000] font-semibold pr-4">
                    {f.q}
                  </span>
                  <CaretRight
                    weight="bold"
                    size={18}
                    className="text-[#D4AF37] flex-shrink-0 transition-transform group-open:rotate-90"
                  />
                </summary>
                <div className="px-6 pb-6 -mt-1 text-[#3C3C3C] leading-[1.85] text-sm sm:text-[15px]">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#800000] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <Heart weight="duotone" size={48} className="text-[#D4AF37] mx-auto mb-5" />
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-5 leading-tight">
            Add the photo booth to your <span className="italic gold-shimmer">wedding date.</span>
          </h2>
          <p className="text-base sm:text-lg text-white/85 mb-9 max-w-2xl mx-auto leading-relaxed">
            One booking, one date, one delivery. Reserve your wedding officiant ceremony with India and add the photo booth experience in a single step.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/booking?service=wedding-officiant&photoBooth=1"
              data-testid="photo-booth-final-book-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-4 rounded-sm font-semibold text-sm uppercase tracking-[0.15em]"
            >
              <CalendarCheck weight="bold" size={18} />
              Book Wedding + Photo Booth
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#800000] transition-all px-7 py-4 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={18} />
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
